export const translations = {
    en: {
        nav: {
            how: "How it works",
            packages: "Packages",
            gallery: "Before/After",
            faq: "FAQ",
            cta: "Start my project",
        },
        hero: {
            eyebrow: "Personalized organization systems",
            h1_a: "From cluttered to functional",
            h1_b: "— without the overwhelm",
            sub: "We design personalized organization systems for your home — so everything has a place and your space finally works for you.",
            cta_primary: "Start My Project",
            cta_secondary: "See Examples",
            beforeLabel: "Before",
            afterLabel: "After",
            delivery: "Delivery 24–48h",
        },
        value: {
            eyebrow: "Value",
            title: "A smarter way to organize your space",
            bullets: [
                { t: "Personalized layout", d: "Based on your real space, not a template." },
                { t: "Clear shopping list", d: "No guessing what to buy — we tell you exactly." },
                { t: "Visual plan", d: "See the result before you lift a single box." },
                { t: "Designed for real life", d: "Not just aesthetics — flow that lasts." },
            ],
        },
        how: {
            eyebrow: "Process",
            title: "Simple. Fast. Done for you.",
            steps: [
                { t: "Show us your space", d: "Upload photos and answer a few quick questions." },
                { t: "We design your system", d: "We create a layout, categories, and shopping plan." },
                { t: "You bring it to life", d: "Follow the plan and transform your space." },
            ],
            delivery: "Delivered in 24–48 hours",
        },
        what: {
            eyebrow: "Deliverables",
            title: "Everything you need to get organized",
            items: [
                "Visual layout of your space",
                "Product & shopping list",
                "Category system (what goes where)",
                "Step-by-step setup guidance",
                "Optional 3D mockup (Premium)",
            ],
        },
        packages: {
            eyebrow: "Plans",
            title: "Pick a plan that fits your space",
            sub: "All plans include layout and category system. Upgrade for shopping lists and 3D.",
            recommended: "Most popular",
            cta: "Start this plan",
            basic: { name: "Basic", tagline: "For simple organization", features: ["Personalized layout", "Category system", "PDF delivery"] },
            standard: { name: "Standard", tagline: "Best balance", features: ["Personalized layout", "Category system", "Full shopping list", "Setup guidance"] },
            premium: { name: "Premium", tagline: "Full transformation", features: ["Everything in Standard", "3D mockup of final space", "Priority design (24h)", "Revisions included"] },
        },
        gallery: {
            eyebrow: "Real results",
            title: "Real transformations",
            sub: "From chaos to clarity. Slide to see the change.",
            filters: { all: "All", garage: "Garage", closet: "Closet", storage: "Storage" },
        },
        who: {
            eyebrow: "Who it's for",
            title: "Designed for real life",
            items: ["Busy families", "Overwhelmed spaces", "People who want clarity, not complexity", "Anyone tired of clutter"],
        },
        faq: {
            eyebrow: "FAQ",
            title: "Answers, fast",
            items: [
                { q: "Do I need exact measurements?", a: "No. Your photos are enough. Windows and room proportions follow your photos, and we never invent footage." },
                { q: "What if I don't know what to buy?", a: "We tell you. Our Standard and Premium plans include a full shopping list with links." },
                { q: "How long does it take?", a: "Blueprints are emailed as a PDF, usually within a few minutes after submit (free) or payment (Plus/Premium)." },
                { q: "Do I need to install anything complicated?", a: "No. We design around real-life setups with simple, achievable steps — no heavy construction." },
            ],
        },
        finalCta: {
            title: "Your space can feel different — fast",
            sub: "Start your personalized plan today and finally create a space that works for you.",
            btn: "Start My Project",
        },
        footer: { tagline: "Design your space. Find your calm.", rights: "All rights reserved.", admin: "Admin" },
        success: {
            checking: "Verifying your payment…",
            paid: "Payment received",
            paidSub: "Your plan is confirmed. We'll begin designing your space.",
            continue: "Continue to questionnaire",
            expired: "This session expired. Please try again.",
            pending: "Payment pending — please wait.",
            backHome: "Back to home",
        },
        admin: {
            login: "Admin login", password: "Password", signIn: "Sign in",
            dashboard: "Dashboard", leads: "Leads", gallery: "Gallery", transactions: "Transactions",
            logout: "Logout", addGallery: "Add transformation", title: "Title", category: "Category",
            beforeUrl: "Before image URL", afterUrl: "After image URL", save: "Save", delete: "Delete",
            noLeads: "No leads yet.", noTx: "No transactions yet.",
        },
        questionnaire: {
            title: "Tell us about your space",
            sub: "Answer a few quick questions so we can design a space that fits your life.",
            step: "Section",
            of: "of",
            next: "Continue",
            back: "Back",
            submit: "Submit project",
            maxThree: "Choose up to 3",
            chooseAll: "Choose all that apply",
            optional: "Optional",
            required: "Required",
            other: "Other",
            otherPlaceholder: "Type your answer…",
            limitReached: "You can pick up to 3.",
            thankYou: "Thank you!",
            thankYouSub: "We've received your project. Our design team will reach out within 24–48 hours with your personalized plan.",
            uploading: "Uploading…",
            tooLarge: "File too large (max 10MB)",
            uploadFailed: "Upload failed. Try again.",
            // Section 1
            spaceTitle: "What space are we designing?",
            spaceSub: "Pick the room you want transformed.",
            spaceOptions: {
                garage: "Garage",
                closet: "Closet",
                pantry: "Pantry",
                storage: "Storage room",
                bedroom: "Bedroom",
                living: "Living room",
                kitchen: "Kitchen",
                office: "Home office",
                other: "Other",
            },
            // Section 2
            bothersTitle: "What bothers you most about this space right now?",
            bothersOptions: {
                clutter: "Too much clutter",
                no_storage: "Not enough storage",
                hard_clean: "Hard to clean",
                stressful: "Feels stressful/overwhelming",
                not_cozy: "Doesn't feel cozy",
                no_function: "Doesn't function well",
                bad_layout: "Poor furniture layout",
                too_dark: "Too dark",
                no_hobby: "No space for hobbies/work",
            },
            // Section 3
            feelingTitle: "What do you want this space to feel like?",
            feelingOptions: {
                calm: "Calm",
                cozy: "Cozy",
                minimal: "Clean / minimal",
                airy: "Airy / open",
                elegant: "Elegant",
                warm: "Warm",
                functional: "Functional",
                modern: "Modern",
                luxurious: "Luxurious",
                practical: "Practical",
            },
            // Section 4
            mustStayTitle: "What MUST stay in the room?",
            mustStaySub: "List furniture or items that need to remain.",
            mustStayPlaceholder: "E.g. King-size bed, antique dresser, treadmill…",
            // Section 5
            storageTitle: "What storage do you need more of?",
            storageOptions: {
                clothing: "Clothing",
                shoes: "Shoes",
                paperwork: "Paperwork",
                hobby: "Hobby / craft items",
                decor: "Decor",
                laundry: "Laundry",
                tools: "Tools",
                sports: "Sports gear",
            },
            // Section 6
            styleTitle: "Style preferences",
            styleSub: "Choose the styles and colors you like most.",
            styleHeading: "Style",
            colorsHeading: "Colors",
            styleOptions: {
                modern: "Modern",
                minimal: "Minimal",
                farmhouse: "Farmhouse",
                traditional: "Traditional",
                scandinavian: "Scandinavian",
                cozy_layered: "Cozy & layered",
                natural: "Natural / organic",
                feminine: "Feminine soft",
                hotel: "Hotel-inspired",
                mixed: "Mixed style",
            },
            colorOptions: {
                warm_neutrals: "Warm neutrals",
                white: "White / light",
                sage: "Sage green",
                earth: "Earth tones",
                blue: "Soft blues",
                dark: "Dark / moody",
                wood: "Wood tones",
                black: "Black accents",
            },
            // Section 7
            budgetTitle: "Budget comfort zone",
            budgetOptions: {
                under_100: "Under $100",
                "100_300": "$100 – 300",
                "300_700": "$300 – 700",
                "700_plus": "$700+",
            },
            // Section 8
            diyTitle: "DIY comfort level",
            diyOptions: {
                very: "Very DIY-friendly",
                simple: "Simple assembly only",
                minimal: "Prefer minimal work",
                hire: "Would hire help if needed",
            },
            // Section 9
            uploadTitle: "Upload photos of your space",
            uploadSub: "Pictures of the room and any closet/storage areas you want modified.",
            uploadCta: "Click or drop images here",
            uploadHelp: "Up to 8 photos · JPG/PNG · 10MB each",
            orientation: {
                landscapeTitle: "This photo is landscape — is the ceiling up?",
                exifTitle: "We straightened this from your phone — is the ceiling up?",
                ambiguousTitle: "Quick check: is the ceiling at the top?",
                body: "Ceiling fans and lights should sit at the top of the frame. Windows should look like windows, not the floor.",
                rotate: "Rotate",
                confirm: "Ceiling is up",
            },
            // Section 10
            finalTitle: "If this room could improve your daily life in ONE way, what would it be?",
            finalPlaceholder: "E.g. I'd start the day calm instead of searching for my keys…",
            // Section 11 (contact)
            contactTitle: "Where should we send your plan?",
            contactSub: "We'll deliver your design within 24–48 hours.",
            name: "Your name",
            email: "Email",
            phone: "Phone (optional)",
            packageTitle: "Choose a plan (optional)",
        },
    },
    es: {
        nav: { how: "Cómo funciona", packages: "Planes", gallery: "Antes/Después", faq: "Preguntas", cta: "Empezar mi proyecto" },
        hero: {
            eyebrow: "Sistemas de organización personalizados",
            h1_a: "Del desorden a lo funcional",
            h1_b: "— sin abrumarte",
            sub: "Diseñamos sistemas de organización personalizados para tu hogar — para que todo tenga su lugar y tu espacio por fin funcione para ti.",
            cta_primary: "Empezar mi proyecto",
            cta_secondary: "Ver ejemplos",
            beforeLabel: "Antes",
            afterLabel: "Después",
            delivery: "Entrega 24–48h",
        },
        value: {
            eyebrow: "Valor",
            title: "Una forma más inteligente de organizar tu espacio",
            bullets: [
                { t: "Distribución personalizada", d: "Basada en tu espacio real, no una plantilla." },
                { t: "Lista de compras clara", d: "Nada de adivinar — te decimos exactamente qué comprar." },
                { t: "Plan visual", d: "Ve el resultado antes de mover una caja." },
                { t: "Diseñado para la vida real", d: "No solo estética — flujo que perdura." },
            ],
        },
        how: {
            eyebrow: "Proceso",
            title: "Simple. Rápido. Hecho por nosotros.",
            steps: [
                { t: "Muéstranos tu espacio", d: "Sube fotos y responde unas preguntas rápidas." },
                { t: "Diseñamos tu sistema", d: "Creamos un plano, categorías y plan de compras." },
                { t: "Tú lo haces realidad", d: "Sigue el plan y transforma tu espacio." },
            ],
            delivery: "Entregado en 24–48 horas",
        },
        what: {
            eyebrow: "Entregables",
            title: "Todo lo que necesitas para organizarte",
            items: [
                "Plano visual de tu espacio",
                "Lista de productos y compras",
                "Sistema de categorías (qué va dónde)",
                "Guía paso a paso",
                "Maqueta 3D opcional (Premium)",
            ],
        },
        packages: {
            eyebrow: "Planes",
            title: "Elige el plan que mejor encaje",
            sub: "Todos incluyen plano y categorías. Mejora para listas y 3D.",
            recommended: "Más elegido",
            cta: "Empezar este plan",
            basic: { name: "Básico", tagline: "Para organización simple", features: ["Distribución personalizada", "Sistema de categorías", "Entrega en PDF"] },
            standard: { name: "Estándar", tagline: "Mejor equilibrio", features: ["Distribución personalizada", "Sistema de categorías", "Lista de compras completa", "Guía de instalación"] },
            premium: { name: "Premium", tagline: "Transformación total", features: ["Todo lo del Estándar", "Maqueta 3D del espacio final", "Diseño prioritario (24h)", "Revisiones incluidas"] },
        },
        gallery: {
            eyebrow: "Resultados reales",
            title: "Transformaciones reales",
            sub: "Del caos a la claridad. Desliza para ver el cambio.",
            filters: { all: "Todo", garage: "Garaje", closet: "Armario", storage: "Despensa" },
        },
        who: {
            eyebrow: "Para quién es",
            title: "Diseñado para la vida real",
            items: ["Familias ocupadas", "Espacios abrumados", "Quienes buscan claridad, no complejidad", "Cansados del desorden"],
        },
        faq: {
            eyebrow: "Preguntas",
            title: "Respuestas rápidas",
            items: [
                { q: "¿Necesito medidas exactas?", a: "No. Con algunas fotos y medidas aproximadas es suficiente para diseñar tu sistema." },
                { q: "¿Qué pasa si no sé qué comprar?", a: "Te lo decimos. Los planes Estándar y Premium incluyen una lista completa con enlaces." },
                { q: "¿Cuánto tarda?", a: "La mayoría de planes se entregan en 24–48 horas. Premium tiene prioridad 24h." },
                { q: "¿Necesito instalar algo complicado?", a: "No. Diseñamos con pasos simples y alcanzables — sin obras ni construcciones." },
            ],
        },
        finalCta: {
            title: "Tu espacio puede sentirse diferente — rápido",
            sub: "Empieza hoy tu plan personalizado y crea por fin un espacio que funcione para ti.",
            btn: "Empezar mi proyecto",
        },
        footer: { tagline: "Diseña tu espacio. Encuentra tu calma.", rights: "Todos los derechos reservados.", admin: "Administrador" },
        success: {
            checking: "Verificando tu pago…",
            paid: "Pago recibido",
            paidSub: "Tu plan está confirmado. Empezamos a diseñar tu espacio.",
            continue: "Continuar al cuestionario",
            expired: "La sesión expiró. Intenta de nuevo.",
            pending: "Pago pendiente — espera un momento.",
            backHome: "Volver al inicio",
        },
        admin: {
            login: "Admin", password: "Contraseña", signIn: "Entrar",
            dashboard: "Panel", leads: "Clientes", gallery: "Galería", transactions: "Transacciones",
            logout: "Salir", addGallery: "Añadir transformación", title: "Título", category: "Categoría",
            beforeUrl: "URL imagen antes", afterUrl: "URL imagen después", save: "Guardar", delete: "Eliminar",
            noLeads: "Sin clientes aún.", noTx: "Sin transacciones aún.",
        },
        questionnaire: {
            title: "Cuéntanos sobre tu espacio",
            sub: "Responde unas preguntas rápidas para que diseñemos un espacio que se ajuste a tu vida.",
            step: "Sección",
            of: "de",
            next: "Continuar",
            back: "Atrás",
            submit: "Enviar proyecto",
            maxThree: "Elige hasta 3",
            chooseAll: "Elige todas las que apliquen",
            optional: "Opcional",
            required: "Requerido",
            other: "Otro",
            otherPlaceholder: "Escribe tu respuesta…",
            limitReached: "Puedes elegir hasta 3.",
            thankYou: "¡Gracias!",
            thankYouSub: "Hemos recibido tu proyecto. Nuestro equipo te enviará tu plan personalizado en 24–48 horas.",
            uploading: "Subiendo…",
            tooLarge: "Archivo muy grande (máx 10MB)",
            uploadFailed: "Error al subir. Intenta de nuevo.",
            spaceTitle: "¿Qué espacio vamos a diseñar?",
            spaceSub: "Elige la habitación que quieres transformar.",
            spaceOptions: {
                garage: "Garaje", closet: "Armario", pantry: "Despensa", storage: "Cuarto de servicio",
                bedroom: "Dormitorio", living: "Sala", kitchen: "Cocina", office: "Oficina en casa", other: "Otro",
            },
            bothersTitle: "¿Qué te molesta más de este espacio ahora?",
            bothersOptions: {
                clutter: "Demasiado desorden",
                no_storage: "Falta de almacenamiento",
                hard_clean: "Difícil de limpiar",
                stressful: "Se siente estresante / agobiante",
                not_cozy: "No se siente acogedor",
                no_function: "No funciona bien",
                bad_layout: "Mala distribución de muebles",
                too_dark: "Demasiado oscuro",
                no_hobby: "Sin espacio para hobbies / trabajo",
            },
            feelingTitle: "¿Cómo quieres que se sienta este espacio?",
            feelingOptions: {
                calm: "Tranquilo", cozy: "Acogedor", minimal: "Limpio / minimalista", airy: "Aireado / abierto",
                elegant: "Elegante", warm: "Cálido", functional: "Funcional", modern: "Moderno",
                luxurious: "Lujoso", practical: "Práctico",
            },
            mustStayTitle: "¿Qué DEBE quedarse en la habitación?",
            mustStaySub: "Lista los muebles o cosas que deben permanecer.",
            mustStayPlaceholder: "Ej: Cama king, cómoda antigua, caminadora…",
            storageTitle: "¿Para qué necesitas más almacenamiento?",
            storageOptions: {
                clothing: "Ropa", shoes: "Zapatos", paperwork: "Papeleo", hobby: "Hobby / manualidades",
                decor: "Decoración", laundry: "Lavandería", tools: "Herramientas", sports: "Equipo deportivo",
            },
            styleTitle: "Preferencias de estilo",
            styleSub: "Elige los estilos y colores que más te gustan.",
            styleHeading: "Estilo",
            colorsHeading: "Colores",
            styleOptions: {
                modern: "Moderno", minimal: "Minimalista", farmhouse: "Farmhouse",
                traditional: "Tradicional", scandinavian: "Escandinavo", cozy_layered: "Acogedor con capas",
                natural: "Natural / orgánico", feminine: "Femenino suave", hotel: "Inspirado en hotel", mixed: "Estilo mixto",
            },
            colorOptions: {
                warm_neutrals: "Neutros cálidos", white: "Blanco / claro", sage: "Verde salvia",
                earth: "Tonos tierra", blue: "Azules suaves", dark: "Oscuro / sobrio",
                wood: "Tonos madera", black: "Detalles negros",
            },
            budgetTitle: "Zona de presupuesto cómodo",
            budgetOptions: { under_100: "Menos de $100", "100_300": "$100 – 300", "300_700": "$300 – 700", "700_plus": "$700+" },
            diyTitle: "Nivel de comodidad DIY",
            diyOptions: {
                very: "Muy cómodo con DIY", simple: "Solo armado simple",
                minimal: "Prefiero poco trabajo", hire: "Contrataría ayuda si es necesario",
            },
            uploadTitle: "Sube fotos de tu espacio",
            uploadSub: "Fotos de la habitación y de los closets / áreas que quieres modificar.",
            uploadCta: "Haz clic o suelta imágenes aquí",
            uploadHelp: "Hasta 8 fotos · JPG/PNG · 10MB cada una",
            orientation: {
                landscapeTitle: "Esta foto es horizontal — ¿el techo está arriba?",
                exifTitle: "Enderezamos esta foto del teléfono — ¿el techo está arriba?",
                ambiguousTitle: "Revisa rápido: ¿el techo está en la parte de arriba?",
                body: "Los ventiladores y luces de techo deben verse arriba. Las ventanas deben verse como ventanas, no como el piso.",
                rotate: "Rotar",
                confirm: "El techo está arriba",
            },
            finalTitle: "Si esta habitación pudiera mejorar tu día a día en UNA sola cosa, ¿cuál sería?",
            finalPlaceholder: "Ej: Empezar el día tranquilo en lugar de buscar las llaves…",
            contactTitle: "¿A dónde te enviamos tu plan?",
            contactSub: "Entregamos tu diseño en 24–48 horas.",
            name: "Tu nombre",
            email: "Correo",
            phone: "Teléfono (opcional)",
            packageTitle: "Elige un plan (opcional)",
        },
    },
    pt: {
        nav: { how: "Como funciona", packages: "Planos", gallery: "Antes/Depois", faq: "Dúvidas", cta: "Começar meu projeto" },
        hero: {
            eyebrow: "Sistemas de organização personalizados",
            h1_a: "Do caos ao funcional",
            h1_b: "— sem sobrecarga",
            sub: "Projetamos sistemas de organização personalizados para sua casa — para que tudo tenha um lugar e seu espaço finalmente funcione para você.",
            cta_primary: "Começar meu projeto",
            cta_secondary: "Ver exemplos",
            beforeLabel: "Antes",
            afterLabel: "Depois",
            delivery: "Entrega 24–48h",
        },
        value: {
            eyebrow: "Valor",
            title: "Uma forma mais inteligente de organizar seu espaço",
            bullets: [
                { t: "Layout personalizado", d: "Baseado no seu espaço real, não em um modelo." },
                { t: "Lista de compras clara", d: "Sem adivinhação — dizemos exatamente o que comprar." },
                { t: "Plano visual", d: "Veja o resultado antes de mover uma caixa." },
                { t: "Feito para a vida real", d: "Não só estética — fluxo que dura." },
            ],
        },
        how: {
            eyebrow: "Processo",
            title: "Simples. Rápido. Feito para você.",
            steps: [
                { t: "Mostre seu espaço", d: "Envie fotos e responda algumas perguntas rápidas." },
                { t: "Projetamos seu sistema", d: "Criamos layout, categorias e plano de compras." },
                { t: "Você coloca em prática", d: "Siga o plano e transforme seu espaço." },
            ],
            delivery: "Entregue em 24–48 horas",
        },
        what: {
            eyebrow: "Entregáveis",
            title: "Tudo que você precisa para se organizar",
            items: [
                "Layout visual do seu espaço",
                "Lista de produtos e compras",
                "Sistema de categorias (o que vai onde)",
                "Guia passo a passo",
                "Mockup 3D opcional (Premium)",
            ],
        },
        packages: {
            eyebrow: "Planos",
            title: "Escolha o plano que se encaixa",
            sub: "Todos incluem layout e categorias. Faça upgrade para listas e 3D.",
            recommended: "Mais popular",
            cta: "Começar este plano",
            basic: { name: "Básico", tagline: "Para organização simples", features: ["Layout personalizado", "Sistema de categorias", "Entrega em PDF"] },
            standard: { name: "Padrão", tagline: "Melhor equilíbrio", features: ["Layout personalizado", "Sistema de categorias", "Lista de compras completa", "Guia de instalação"] },
            premium: { name: "Premium", tagline: "Transformação total", features: ["Tudo do Padrão", "Mockup 3D do espaço final", "Design prioritário (24h)", "Revisões incluídas"] },
        },
        gallery: {
            eyebrow: "Resultados reais",
            title: "Transformações reais",
            sub: "Do caos à clareza. Deslize para ver a mudança.",
            filters: { all: "Tudo", garage: "Garagem", closet: "Closet", storage: "Despensa" },
        },
        who: {
            eyebrow: "Para quem é",
            title: "Feito para a vida real",
            items: ["Famílias ocupadas", "Espaços sobrecarregados", "Quem quer clareza, não complexidade", "Cansados da bagunça"],
        },
        faq: {
            eyebrow: "Dúvidas",
            title: "Respostas rápidas",
            items: [
                { q: "Preciso de medidas exatas?", a: "Não. Algumas fotos e medidas aproximadas são suficientes para projetar seu sistema." },
                { q: "E se eu não souber o que comprar?", a: "A gente te diz. Planos Padrão e Premium incluem lista completa com links." },
                { q: "Quanto tempo leva?", a: "A maioria dos planos é entregue em 24–48 horas. Premium tem prioridade 24h." },
                { q: "Preciso instalar algo complicado?", a: "Não. Projetamos com passos simples — sem obras pesadas." },
            ],
        },
        finalCta: {
            title: "Seu espaço pode se sentir diferente — rápido",
            sub: "Comece seu plano personalizado hoje e finalmente tenha um espaço que funciona.",
            btn: "Começar meu projeto",
        },
        footer: { tagline: "Projete seu espaço. Encontre sua calma.", rights: "Todos os direitos reservados.", admin: "Admin" },
        success: {
            checking: "Verificando seu pagamento…",
            paid: "Pagamento recebido",
            paidSub: "Seu plano está confirmado. Começamos o design.",
            continue: "Continuar ao questionário",
            expired: "A sessão expirou. Tente novamente.",
            pending: "Pagamento pendente — aguarde.",
            backHome: "Voltar para o início",
        },
        admin: {
            login: "Admin", password: "Senha", signIn: "Entrar",
            dashboard: "Painel", leads: "Leads", gallery: "Galeria", transactions: "Transações",
            logout: "Sair", addGallery: "Adicionar transformação", title: "Título", category: "Categoria",
            beforeUrl: "URL imagem antes", afterUrl: "URL imagem depois", save: "Salvar", delete: "Excluir",
            noLeads: "Sem leads ainda.", noTx: "Sem transações ainda.",
        },
        questionnaire: {
            title: "Conte-nos sobre seu espaço",
            sub: "Responda algumas perguntas rápidas para criarmos um espaço que combine com sua vida.",
            step: "Seção",
            of: "de",
            next: "Continuar",
            back: "Voltar",
            submit: "Enviar projeto",
            maxThree: "Escolha até 3",
            chooseAll: "Escolha todas que se aplicam",
            optional: "Opcional",
            required: "Obrigatório",
            other: "Outro",
            otherPlaceholder: "Digite sua resposta…",
            limitReached: "Você pode escolher até 3.",
            thankYou: "Obrigado!",
            thankYouSub: "Recebemos seu projeto. Nossa equipe enviará seu plano personalizado em 24–48 horas.",
            uploading: "Enviando…",
            tooLarge: "Arquivo muito grande (máx 10MB)",
            uploadFailed: "Falha no envio. Tente novamente.",
            spaceTitle: "Qual espaço vamos projetar?",
            spaceSub: "Escolha o cômodo que você quer transformar.",
            spaceOptions: {
                garage: "Garagem", closet: "Closet", pantry: "Despensa", storage: "Depósito",
                bedroom: "Quarto", living: "Sala", kitchen: "Cozinha", office: "Escritório", other: "Outro",
            },
            bothersTitle: "O que mais te incomoda nesse espaço agora?",
            bothersOptions: {
                clutter: "Muita bagunça",
                no_storage: "Falta de armazenamento",
                hard_clean: "Difícil de limpar",
                stressful: "Estressante / sobrecarregado",
                not_cozy: "Não é aconchegante",
                no_function: "Não funciona bem",
                bad_layout: "Móveis mal distribuídos",
                too_dark: "Muito escuro",
                no_hobby: "Sem espaço para hobbies / trabalho",
            },
            feelingTitle: "Como você quer que esse espaço se sinta?",
            feelingOptions: {
                calm: "Calmo", cozy: "Aconchegante", minimal: "Limpo / minimalista", airy: "Arejado / aberto",
                elegant: "Elegante", warm: "Caloroso", functional: "Funcional", modern: "Moderno",
                luxurious: "Luxuoso", practical: "Prático",
            },
            mustStayTitle: "O que DEVE continuar no cômodo?",
            mustStaySub: "Liste os móveis ou itens que precisam permanecer.",
            mustStayPlaceholder: "Ex: Cama king, cômoda antiga, esteira…",
            storageTitle: "De qual tipo de armazenamento você precisa mais?",
            storageOptions: {
                clothing: "Roupas", shoes: "Sapatos", paperwork: "Papelada", hobby: "Hobby / artesanato",
                decor: "Decoração", laundry: "Roupas para lavar", tools: "Ferramentas", sports: "Equipamento esportivo",
            },
            styleTitle: "Preferências de estilo",
            styleSub: "Escolha os estilos e cores que mais gosta.",
            styleHeading: "Estilo",
            colorsHeading: "Cores",
            styleOptions: {
                modern: "Moderno", minimal: "Minimalista", farmhouse: "Farmhouse",
                traditional: "Tradicional", scandinavian: "Escandinavo", cozy_layered: "Aconchegante em camadas",
                natural: "Natural / orgânico", feminine: "Feminino suave", hotel: "Inspirado em hotel", mixed: "Estilo misto",
            },
            colorOptions: {
                warm_neutrals: "Neutros quentes", white: "Branco / claro", sage: "Verde sálvia",
                earth: "Tons terrosos", blue: "Azuis suaves", dark: "Escuro / dramático",
                wood: "Tons de madeira", black: "Detalhes pretos",
            },
            budgetTitle: "Faixa de orçamento confortável",
            budgetOptions: { under_100: "Menos de $100", "100_300": "$100 – 300", "300_700": "$300 – 700", "700_plus": "$700+" },
            diyTitle: "Nível de conforto com DIY",
            diyOptions: {
                very: "Muito confortável com DIY", simple: "Apenas montagem simples",
                minimal: "Prefiro pouco trabalho", hire: "Contrataria ajuda se necessário",
            },
            uploadTitle: "Envie fotos do seu espaço",
            uploadSub: "Fotos do cômodo e dos closets / áreas de armazenamento que quer modificar.",
            uploadCta: "Clique ou arraste imagens aqui",
            uploadHelp: "Até 8 fotos · JPG/PNG · 10MB cada",
            orientation: {
                landscapeTitle: "Esta foto está em paisagem — o teto está em cima?",
                exifTitle: "Endireitamos esta foto do celular — o teto está em cima?",
                ambiguousTitle: "Confira: o teto está na parte de cima?",
                body: "Ventiladores e luzes de teto devem ficar no topo do quadro. Janelas devem parecer janelas, não o chão.",
                rotate: "Girar",
                confirm: "O teto está em cima",
            },
            finalTitle: "Se esse cômodo pudesse melhorar seu dia a dia em UMA coisa, qual seria?",
            finalPlaceholder: "Ex: Começar o dia calmo em vez de procurar minhas chaves…",
            contactTitle: "Para onde enviamos seu plano?",
            contactSub: "Entregamos seu design em 24–48 horas.",
            name: "Seu nome",
            email: "E-mail",
            phone: "Telefone (opcional)",
            packageTitle: "Escolha um plano (opcional)",
        },
    },
};

export const LANGS = [
    { code: "en", label: "EN" },
    { code: "es", label: "ES" },
    { code: "pt", label: "PT" },
];
